import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, MessageCircle } from 'lucide-react';
import { useSeo, SITE_NAME } from '@/lib/seo';
import { Section } from '@/components/layout/Section';
import { Button } from '@/components/ui/button';
import FeaturedImage from '@/components/FeaturedImage';
import GalleryStrip from '@/components/GalleryStrip';
import { useSite, useWhatsappUrl } from '@/context/SiteContext';
import { TEST_IDS } from '@/constants/testIds/floreal';

const GARDEN_SERVICES = [
    'Progettazione giardini',
    'Realizzazione aree verdi',
    'Selezione piante',
    'Riqualificazione spazi esterni',
    'Manutenzione periodica',
];

export default function Giardini() {
    const { t } = useSite();
    const whatsappUrl = useWhatsappUrl();
    useSeo({
        title: `${SITE_NAME} — Giardini`,
        description: "Progettazione e manutenzione giardini, aree verdi, terrazzi a Lomazzo e nel Comasco. Spazi belli, funzionali e duraturi.",
    });
    return (
        <div data-testid={TEST_IDS.pageGiardini}>
            {/* Hero */}
            <section className="relative pt-32 sm:pt-40 pb-16 sm:pb-20">
                <div className="container-atelier grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <p className="eyebrow mb-4">{t('giardini.hero.eyebrow', 'Giardini')}</p>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                            {t('giardini.hero.title', 'Progettiamo spazi verdi da vivere.')}
                        </h1>
                        <p className="mt-7 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-prose">
                            {t('giardini.hero.description', 'Ci occupiamo di tutto, dalla progettazione alla manutenzione.')}
                        </p>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="lg:col-span-6"
                    >
                        <FeaturedImage
                            slot="hero_giardini"
                            alt="Progetto di giardino realizzato da Floreal"
                            priority
                            className="rounded-xl aspect-[4/5] w-full shadow-soft"
                            fallbackLabel="Giardini"
                        />
                    </motion.div>
                </div>
            </section>

            {/* Servizi — only title (no lead) */}
            <Section title={t('giardini.services.title', 'I nostri servizi')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {GARDEN_SERVICES.map((s, i) => (
                        <motion.div
                            key={s}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.35, delay: (i % 3) * 0.05 }}
                            className="flex items-center gap-3 rounded-xl border border-border bg-card px-5 py-4"
                        >
                            <span className="h-7 w-7 rounded-full bg-atelier-sage/20 flex items-center justify-center shrink-0">
                                <Check className="h-3.5 w-3.5 text-atelier-sage-ink" strokeWidth={2.5} />
                            </span>
                            <span className="text-sm sm:text-[15px]">{s}</span>
                        </motion.div>
                    ))}
                </div>
            </Section>

            {/* Photo strip */}
            <Section className="py-10 sm:py-14">
                <GalleryStrip
                    category="giardini"
                    limit={4}
                    tileClass="rounded-lg aspect-[4/5]"
                    ariaLabel="Progetti di giardini Floreal"
                />
            </Section>

            {/* CTA — stesso stile della Home */}
            <Section className="py-12 sm:py-16">
                <div className="rounded-xl bg-atelier-ivory border border-atelier-sage/25 px-6 sm:px-10 py-12 sm:py-16 text-center">
                    <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight max-w-2xl mx-auto leading-[1.1]">
                        {t('giardini.cta.title', 'Iniziamo un progetto insieme?')}
                    </h3>
                    <p className="mt-4 text-muted-foreground max-w-xl mx-auto leading-relaxed">
                        {t('giardini.cta.description', 'Raccontaci il tuo spazio: ti aiuteremo a immaginare e realizzare il giardino giusto per te.')}
                    </p>
                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        <Button asChild size="lg" className="h-12 px-6">
                            <Link to="/contatti#form">{t('giardini.cta.btn_info', 'Richiedi informazioni')}</Link>
                        </Button>
                        <Button asChild size="lg" variant="outline" className="h-12 px-6 border-foreground/20 bg-background hover:bg-secondary">
                            <a href={whatsappUrl} target="_blank" rel="noreferrer">
                                <MessageCircle className="h-4 w-4 mr-2" /> {t('giardini.cta.btn_whatsapp', 'Scrivici su WhatsApp')}
                            </a>
                        </Button>
                    </div>
                </div>
            </Section>
        </div>
    );
}
